import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, AccountTree, Search } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Departamento, Subdepartamento, Veiculo } from '../types';
import { departamentoService } from '../services/departamentoService';
import { subdepartamentoService } from '../services/subdepartamentoService';
import { veiculoService } from '../services/veiculoService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const schemaDept = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  descricao: yup.string().optional(),
}).required();

const schemaSub = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  descricao: yup.string().optional(),
  departamentoId: yup.number().required('Unidade é obrigatória'),
}).required();

const Departamentos: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [subdepartamentos, setSubdepartamentos] = useState<Subdepartamento[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [filtro, setFiltro] = useState('');
  const [erro, setErro] = useState('');
  const [avisoOpen, setAvisoOpen] = useState(false);
  const [avisoMsg, setAvisoMsg] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [openDept, setOpenDept] = useState(false);
  const [editingDept, setEditingDept] = useState<Departamento | null>(null);
  const [confirmDeleteDept, setConfirmDeleteDept] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

  const [openSub, setOpenSub] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [veicModalOpen, setVeicModalOpen] = useState(false);
  const [veicModalDept, setVeicModalDept] = useState<Departamento | null>(null);
  const [subModalDept, setSubModalDept] = useState<Departamento | null>(null);
  const [editingSub, setEditingSub] = useState<Subdepartamento | null>(null);
  const [confirmDeleteSub, setConfirmDeleteSub] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

  const { register: registerDept, handleSubmit: handleSubmitDept, reset: resetDept, formState: { errors: errorsDept } } = useForm({
    resolver: yupResolver(schemaDept),
  });

  const { register: registerSub, handleSubmit: handleSubmitSub, reset: resetSub, formState: { errors: errorsSub } } = useForm({
    resolver: yupResolver(schemaSub),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deps, subs, veics] = await Promise.all([
        departamentoService.listar(),
        subdepartamentoService.listar(),
        veiculoService.listar(),
      ]);
      setDepartamentos(deps);
      setSubdepartamentos(subs);
      setVeiculos(veics);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const handleOpenDept = (departamento?: Departamento) => {
    if (departamento) {
      setEditingDept(departamento);
      resetDept({ nome: departamento.nome, descricao: departamento.descricao || '' });
    } else {
      setEditingDept(null);
      resetDept({ nome: '', descricao: '' });
    }
    setOpenDept(true);
  };

  const handleCloseDept = () => {
    setOpenDept(false);
    setEditingDept(null);
    resetDept();
    setErro('');
  };

  const onSubmitDept = async (data: any) => {
    setSalvando(true);
    setErro('');
    try {
      if (editingDept?.id) {
        await departamentoService.atualizar(editingDept.id, data);
      } else {
        await departamentoService.cadastrar(data);
      }
      await loadData();
      handleCloseDept();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const confirmDeleteDeptAction = async () => {
    if (!confirmDeleteDept.id) return;
    try {
      await departamentoService.deletar(confirmDeleteDept.id);
      await loadData();
      setConfirmDeleteDept({ open: false, id: null });
    } catch (error) {
      const msg = getErrorMessage(error);
      setAvisoMsg(msg);
      setAvisoOpen(true);
      setConfirmDeleteDept({ open: false, id: null });
    }
  };

  const handleOpenSub = (sub?: Subdepartamento) => {
    if (sub) {
      setEditingSub(sub);
      resetSub({ nome: sub.nome, descricao: sub.descricao || '', departamentoId: sub.departamentoId });
    } else {
      setEditingSub(null);
      resetSub({ nome: '', descricao: '', departamentoId: departamentos[0]?.id ? Number(departamentos[0].id) : undefined } as any);
    }
    setOpenSub(true);
  };

  const handleOpenSubParaDept = (dept: Departamento) => {
    setEditingSub(null);
    resetSub({ nome: '', descricao: '', departamentoId: dept.id ? Number(dept.id) : undefined } as any);
    setOpenSub(true);
  };

  const handleCloseSub = () => {
    setOpenSub(false);
    setEditingSub(null);
    resetSub();
    setErro('');
  };

  const onSubmitSub = async (data: any) => {
    setSalvando(true);
    setErro('');
    try {
      if (editingSub?.id) {
        await subdepartamentoService.atualizar(editingSub.id, data);
      } else {
        await subdepartamentoService.cadastrar(data);
      }
      await loadData();
      handleCloseSub();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const confirmDeleteSubAction = async () => {
    if (!confirmDeleteSub.id) return;
    try {
      await subdepartamentoService.deletar(confirmDeleteSub.id);
      await loadData();
      setConfirmDeleteSub({ open: false, id: null });
    } catch (error) {
      const msg = getErrorMessage(error);
      setAvisoMsg(msg);
      setAvisoOpen(true);
      setConfirmDeleteSub({ open: false, id: null });
    }
  };

  const getSubdepartamentosPorDept = (deptId?: number) => {
    if (!deptId) return [];
    return subdepartamentosFiltrados.filter(s => s.departamentoId === deptId);
  };

  const getVeiculosPorDept = (deptId?: number) =>
    deptId ? veiculos.filter(v => v.departamentoId === deptId).length : 0;

  const getVeiculosPorSub = (subId?: number) =>
    subId ? veiculos.filter(v => v.subdepartamentoId === subId).length : 0;

  const abrirSubModal = (dept: Departamento) => {
    setSubModalDept(dept);
    setSubModalOpen(true);
  };

  const fecharSubModal = () => {
    setSubModalOpen(false);
    setSubModalDept(null);
  };

  const abrirVeicModal = (dept: Departamento) => {
    setVeicModalDept(dept);
    setVeicModalOpen(true);
  };

  const fecharVeicModal = () => {
    setVeicModalOpen(false);
    setVeicModalDept(null);
  };

  const termo = filtro.trim().toLowerCase();
  const departamentosFiltrados = termo
    ? departamentos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : departamentos;
  const subdepartamentosFiltrados = termo
    ? subdepartamentos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : subdepartamentos;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Departamentos</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDept()}>
            Nova Unidade
          </Button>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Unidades</TableCell>
                <TableCell>Veículos</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departamentosFiltrados.map((d) => (
                <TableRow key={d.id}>
                  <TableCell><strong>{d.nome}</strong></TableCell>
                  <TableCell>
                    <Chip size="small" icon={<AccountTree />} label={`${getSubdepartamentosPorDept(d.id).length}`} onClick={() => abrirSubModal(d)} sx={{ cursor: 'pointer' }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`${getVeiculosPorDept(d.id)}`} onClick={() => abrirVeicModal(d)} sx={{ cursor: 'pointer' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={d.ativo !== false ? 'Ativo' : 'Inativo'} color={d.ativo !== false ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDept(d)}><Edit /></IconButton>
                    <IconButton size="small" color="error" title="Excluir (somente admin; sem dados vinculados)" onClick={() => d.id && setConfirmDeleteDept({ open: true, id: d.id })}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      <Dialog open={openDept} onClose={handleCloseDept} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDept ? 'Editar Unidade' : 'Nova Unidade'}</DialogTitle>
        <DialogContent>
          <form id="dept-form" onSubmit={handleSubmitDept(onSubmitDept)}>
            <TextField fullWidth label="Nome" {...registerDept('nome')} error={!!errorsDept.nome} helperText={errorsDept.nome?.message as string} margin="normal" />
            <TextField fullWidth label="Descrição" {...registerDept('descricao')} margin="normal" multiline rows={2} />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDept}>Cancelar</Button>
          <Button type="submit" form="dept-form" variant="contained" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSub} onClose={handleCloseSub} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSub ? 'Editar Subdepartamento' : 'Novo Subdepartamento'}</DialogTitle>
        <DialogContent>
          <form id="sub-form" onSubmit={handleSubmitSub(onSubmitSub)}>
            {subModalDept ? (
              <TextField fullWidth label="Unidade" value={subModalDept.nome} margin="normal" disabled />
            ) : (
              <TextField select fullWidth label="Unidade" {...registerSub('departamentoId')} error={!!errorsSub.departamentoId} helperText={errorsSub.departamentoId?.message as string} margin="normal" SelectProps={{ native: true }}>
                <option value=""></option>
                {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </TextField>
            )}
            <TextField fullWidth label="Nome" {...registerSub('nome')} error={!!errorsSub.nome} helperText={errorsSub.nome?.message as string} margin="normal" />
            <TextField fullWidth label="Descrição" {...registerSub('descricao')} margin="normal" multiline rows={2} />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSub}>Cancelar</Button>
          <Button type="submit" form="sub-form" variant="contained" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subModalOpen} onClose={fecharSubModal} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle>Unidades - {subModalDept?.nome}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 1, mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => { if (subModalDept) handleOpenSubParaDept(subModalDept); }}>
              Nova Unidade
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Veículos</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSubdepartamentosPorDept(subModalDept?.id).map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell><strong>{sub.nome}</strong></TableCell>
                    <TableCell>
                      <Chip size="small" label={`${getVeiculosPorSub(sub.id)}`} />
                    </TableCell>
                    <TableCell>
                      <Chip label={sub.ativo !== false ? 'Ativo' : 'Inativo'} color={sub.ativo !== false ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenSub(sub)}><Edit /></IconButton>
                      <IconButton size="small" color="error" title="Excluir (somente admin; sem dados vinculados)" onClick={() => sub.id && setConfirmDeleteSub({ open: true, id: sub.id })}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {getSubdepartamentosPorDept(subModalDept?.id).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">Nenhum subdepartamento cadastrado.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharSubModal}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={veicModalOpen} onClose={fecharVeicModal} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle>Veículos - {veicModalDept?.nome}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Placa</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Marca</TableCell>
                  <TableCell>Ano</TableCell>
                  <TableCell>Km Atual</TableCell>
                  <TableCell>Combustível</TableCell>
                  <TableCell>Subdepartamento</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {veiculos.filter(v => v.departamentoId === veicModalDept?.id).map((v) => (
                  <TableRow key={v.id}>
                    <TableCell><strong>{v.placa}</strong></TableCell>
                    <TableCell>{v.modelo}</TableCell>
                    <TableCell>{v.marca}</TableCell>
                    <TableCell>{v.ano}</TableCell>
                    <TableCell>{v.quilometragemAtual?.toLocaleString('pt-BR')} km</TableCell>
                    <TableCell>{v.tipoCombustivel}</TableCell>
                    <TableCell>{v.subdepartamentoNome || '-'}</TableCell>
                    <TableCell>
                      <Chip label={v.status === 'ATIVO' ? 'Ativo' : v.status === 'EM_MANUTENCAO' ? 'Em Manutenção' : 'Inativo'} color={v.status === 'ATIVO' ? 'success' : v.status === 'EM_MANUTENCAO' ? 'warning' : 'error'} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
                {veiculos.filter(v => v.departamentoId === veicModalDept?.id).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">Nenhum veículo vinculado.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharVeicModal}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={avisoOpen} onClose={() => setAvisoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aviso</DialogTitle>
        <DialogContent>
          <Alert severity="warning">{avisoMsg}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvisoOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={confirmDeleteDept.open} title="Confirmar exclusão" message="Tem certeza que deseja excluir esta unidade? Só é possível excluir se não houver subdepartamentos, veículos ou abastecimentos vinculados." confirmText="Excluir" severity="error" onConfirm={confirmDeleteDeptAction} onCancel={() => setConfirmDeleteDept({ open: false, id: null })} />
      <ConfirmDialog open={confirmDeleteSub.open} title="Confirmar exclusão" message="Tem certeza que deseja excluir este subdepartamento? Só é possível excluir se não houver veículos ou abastecimentos vinculados." confirmText="Excluir" severity="error" onConfirm={confirmDeleteSubAction} onCancel={() => setConfirmDeleteSub({ open: false, id: null })} />
    </Container>
  );
};

export default Departamentos;
